import { useTutorialContext } from 'context/TutorialContext';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { Close } from '@assets/icons';
import { IconLink } from '@assets/icons/IconLink';
import { UnstyledButton } from '@components/UnstyledButton/UnstyledButton';
import { RootState } from '@lib/redux.lib/store';
import { getClientEvironmentId } from '@lib/utils/clientEnvironment';
import theme from '@styles/theme';

import { ProcessStepper } from './ProcessStepper';

type GeneralInfoProps = {
  currentStepIndex: number;
};

export const GeneralInfo = ({ currentStepIndex }: GeneralInfoProps) => {
  const {
    bookingProcessModalVisibility,
    bookingProcessTutorialExperienceTrigger,
  } = useTutorialContext();
  const bookingStatusInfoUrl = useBookingStatusesInfoUrl();

  return (
    <StyledContainer>
      <StyledSubContainer>
        {bookingProcessTutorialExperienceTrigger === 'manual' && (
          <StyledCloseContainer>
            <UnstyledButton onClick={bookingProcessModalVisibility.hideModal}>
              <Close stroke={theme.palette.stroke[2]} />
            </UnstyledButton>
          </StyledCloseContainer>
        )}

        <StyledHeading>Our casting process</StyledHeading>
        <StyledParagraph>
          A brief overview of our 3-step process and the{' '}
          <span>actions you’ll need to take.</span>
        </StyledParagraph>

        <StyledLink href={bookingStatusInfoUrl} target='_blank'>
          <span>See full list of statuses</span>
          <IconLink />
        </StyledLink>
      </StyledSubContainer>

      <ProcessStepper currentStepIndex={currentStepIndex} />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  background-color: ${({ theme }) => theme.palette.primary[15]};
  height: 100%;
  padding: 40px;
  width: 300px;
  border-right: 1px solid ${({ theme }) => theme.palette.stroke[0]};

  @media (max-width: 960px) {
    width: 100%;
    padding: 24px 20px 24px;
    height: auto;
    border-right: none;
  }
`;

const StyledSubContainer = styled.div`
  padding: 0;

  @media (max-width: 960px) {
    padding: 0 4px 0;
  }
`;

const StyledHeading = styled.h1`
  margin: 0;
  padding: 0;
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
`;

const StyledParagraph = styled.p`
  margin: 0;
  padding: 0;
  margin-top: 12px;

  @media (max-width: 960px) {
    margin-top: 16px;
  }

  span {
    font-weight: 600;
  }
`;

const StyledLink = styled.a`
  margin-top: 12px;
  display: flex;
  gap: 4px;
  align-items: center;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: auto;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
  color: ${({ theme }) => theme.palette.text[0]};

  &:hover {
    color: ${({ theme }) => theme.palette.text[0]};
  }

  @media (max-width: 960px) {
    margin-top: 8px;
  }
`;

const StyledCloseContainer = styled.div`
  display: none;

  padding-bottom: 12px;

  button {
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;

    svg {
      width: 100%;
      height: 100%;
    }
  }

  @media (max-width: 960px) {
    display: flex;
  }
`;

const useBookingStatusesInfoUrl = () => {
  const clientEnvironmentId = getClientEvironmentId();
  const selectedCountry = useSelector(
    (state: RootState) => state.app.user.profile?.selected_country ?? 'US'
  );

  return (
    BOOKING_STATUSES_URL_INFO_BY_CLIENT_ID[clientEnvironmentId]?.[
      selectedCountry
    ] ?? BOOKING_STATUSES_URL_BASE_EXTERNAL
  );
};

type HC_MAPPER = {
  [key: number]: {
    [key in ProductionCountry]: string;
  };
};

const BOOKING_STATUSES_URL_INFO_BY_CLIENT_ID: HC_MAPPER = {
  // `1` is for Alessi Hartigan
  1: {
    US: 'https://support.alessihartigancasting.com/hc/en-us/articles/11492162288015-What-Does-Each-Booking-Status-Mean-US',
    UK: 'https://support.alessihartigancasting.com/hc/en-us/articles/11518517393935-What-Does-Each-Booking-Status-Mean-UK',
  },
  // `2` is for Everyset
  2: {
    US: 'https://support.everyset.com/hc/en-us/articles/11664550888463-What-Does-Each-Booking-Status-Mean-US',
    UK: 'https://support.everyset.com/hc/en-us/articles/11736275014543-What-Does-Each-Booking-Status-Mean-UK',
  },
};

const BOOKING_STATUSES_URL_BASE_EXTERNAL =
  'https://tech-app-support.zendesk.com/hc/en-us/articles/12434601288719-What-Does-Each-Booking-Status-Mean';
